import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Star } from "lucide-react";

/**
 * TemplateErrorPanel (SOUND-785) — a refused workflow-template action, shown in
 * FULL and kept on screen until dismissed. Toasts vanish; a 409 that says "make
 * another variant the default first" has to stay readable while the user does
 * exactly that.
 *
 * `error` is the model from utilities/workflowVariants.describeTemplateError:
 *   { title, message, code, details[], variants[] }
 * `variants` is the list the user needs to act on — the live variants for
 * DEFAULT_VARIANT_REQUIRED (each with a "Make default" action when
 * `onMakeDefault` is given), or the existing variants for VARIANT_KEY_REQUIRED.
 */
export default function TemplateErrorPanel({ error, onDismiss, onMakeDefault, busyTemplateId }) {
  if (!error) return null;
  const { title, message, code, details = [], variants = [] } = error;
  return (
    <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          {title}
        </div>
        {onDismiss && (
          <button className="text-xs text-muted-foreground hover:text-foreground" onClick={onDismiss}>
            Dismiss
          </button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
      {details.length > 0 && (
        <ul className="mt-2 list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
          {details.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
      {variants.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {code === "DEFAULT_VARIANT_REQUIRED" ? "Other live variants" : "Variants of this request type"}
          </div>
          <ul className="space-y-1">
            {variants.map((v) => (
              <li
                key={v.templateId || v.variantKey}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm"
              >
                {v.templateId ? (
                  <Link to={`/settings/workflows/${v.templateId}`} className="font-medium text-foreground hover:underline">
                    {v.variantLabel || v.variantKey}
                  </Link>
                ) : (
                  <span className="font-medium text-foreground">{v.variantLabel || v.variantKey}</span>
                )}
                <span className="font-mono text-[11px] text-muted-foreground">{v.variantKey}</span>
                {v.isDefault && (
                  <span className="badge badge-brand inline-flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    Default
                  </span>
                )}
                {onMakeDefault && v.templateId && !v.isDefault && (
                  <button
                    className="btn btn-outline ml-auto h-7 px-2 text-xs"
                    disabled={busyTemplateId === v.templateId}
                    onClick={() => onMakeDefault(v)}
                  >
                    Make this the default
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
